import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  useTree,
  type TreeAssetNode,
  type TreeClientNode,
  type TreeProjectNode,
  type TreeSessionNode,
} from '../hooks/useApi';
import { CreateProjectModal } from './CreateProjectModal';
import { SECTIONS, getSectionForSubtype, type SectionDef, type SectionKey } from '../lib/sections';

const STORAGE_KEY = 'xlv:sidebar:expanded';

const KIND_ICONS: Record<string, string> = {
  image_360: '🌐',
  image_2d: '🖼',
  video: '🎬',
  avatar: '🗣',
  audio: '🎵',
  three_d: '🧊',
  mixed: '✨',
};

function agentLabel(slug: string): string {
  const map: Record<string, string> = {
    // xVision (producción) — agentes especializados por tipo de asset (Capa 3)
    'xvision-360': '🌐 Imagen 360',
    'xvision-personaje': '👤 Personajes',
    'xvision-environment': '🏞️ Escenarios',
    'xvision-utilidades': '🛠️ Utilidades',
    'xvision-armonizador': '✨ Armonizador',
    'xvision-voz': '🎤 Voces',
    'xvision-video': '🎬 Videos',
    'xvision-lipsync': '🗣️ Lipsync',
    // fal.ai general (research / consultoría) — Capa 2
    'fal-360-expert': 'Sesión 360',
    'fal-image-expert': 'Sesión imagen',
    'fal-video-expert': 'Sesión video',
    'fal-audio-expert': 'Sesión audio',
    'fal-avatar-expert': 'Sesión avatar',
    'fal-3d-expert': 'Sesión 3D',
    'fal-pipeline-orchestrator': 'Pipeline multi-paso',
    'fal-prompt-engineer': 'Sesión prompts',
    'fal-cost-auditor': 'Auditoría de costos',
    'fal-model-selector': 'Recomendador de modelos',
  };
  return map[slug] ?? slug;
}

function loadExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function saveExpanded(set: Set<string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
}

const COLLAPSED_KEY = 'xlv:sidebar:collapsed';

export function Sidebar() {
  const { data: tree, isLoading } = useTree();
  const [expanded, setExpanded] = useState<Set<string>>(() => loadExpanded());
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  });
  const [showCreate, setShowCreate] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  };

  // Auto-expand cliente, proyecto y sección del nodo activo según la URL
  useEffect(() => {
    if (!tree) return;
    const next = new Set(expanded);
    let changed = false;
    const add = (key: string) => {
      if (!next.has(key)) { next.add(key); changed = true; }
    };
    for (const c of tree) {
      for (const p of c.projects) {
        const inProject = location.pathname.includes(`/projects/${p.slug}`);
        for (const a of p.assets) {
          const inInstance = inProject && location.pathname.includes(`/instances/${a.slug}`);
          const inChat = a.sessions.some((s) => location.pathname.includes(`/chats/${s.id}`));
          if (inInstance || inChat) {
            add(`c:${c.client}`);
            add(`p:${p.id}`);
            add(`s:${p.id}:${getSectionForSubtype(a.subtype)}`);
            add(`a:${a.id}`);
          }
        }
        if (inProject) {
          add(`c:${c.client}`);
          add(`p:${p.id}`);
        }
      }
    }
    if (changed) {
      setExpanded(next);
      saveExpanded(next);
    }
  }, [tree, location.pathname]);

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveExpanded(next);
      return next;
    });
  };

  const matchesSearch = (text: string) =>
    !search || text.toLowerCase().includes(search.toLowerCase());

  const filtered: TreeClientNode[] = (tree ?? [])
    .map((c) => ({
      ...c,
      projects: c.projects
        .map((p) => ({
          ...p,
          assets: p.assets
            .map((a) => ({
              ...a,
              sessions: a.sessions.filter(
                (s) => matchesSearch(s.title ?? '') || matchesSearch(agentLabel(s.agentSlug)),
              ),
            }))
            .filter(
              (a) =>
                matchesSearch(a.name) ||
                a.sessions.length > 0 ||
                matchesSearch(p.name) ||
                matchesSearch(c.client),
            ),
        }))
        .filter(
          (p) => matchesSearch(p.name) || p.assets.length > 0 || matchesSearch(c.client),
        ),
    }))
    .filter(
      (c) => matchesSearch(c.client) || c.projects.length > 0,
    );

  if (collapsed) {
    return (
      <aside className="w-10 border-r border-slate-800 bg-slate-900/60 flex flex-col items-center py-3">
        <button
          onClick={toggleCollapsed}
          className="w-7 h-7 rounded text-slate-400 hover:text-vision hover:bg-slate-800 flex items-center justify-center"
          title="Expandir menú"
        >
          ▶
        </button>
      </aside>
    );
  }

  return (
    <aside className="w-72 border-r border-slate-800 bg-slate-900/60 flex flex-col">
      <div className="p-3 border-b border-slate-800 flex items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar…"
          className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-vision"
        />
        <button
          onClick={toggleCollapsed}
          className="w-7 h-7 rounded text-slate-400 hover:text-vision hover:bg-slate-800 flex items-center justify-center shrink-0"
          title="Colapsar menú"
        >
          ◀
        </button>
      </div>

      {/* Sidebar único: proyectos arriba, después secciones transversales
          (Bibliotecas, Herramientas, Admin). Decisión 2026-04-29 — el
          ConfigMenu del header se eliminó porque los items vivían lejos
          de los proyectos y obligaba a recordar dónde estaba cada cosa. */}

      <div className="px-2 pt-2 pb-1">
        <button
          onClick={() => setShowCreate(true)}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-vision-dark hover:bg-vision text-white shadow font-medium"
          title="Crear un proyecto nuevo"
        >
          <span className="text-base">＋</span>
          <span>Nuevo proyecto</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-0.5 text-sm">
        {isLoading && <div className="text-slate-500 text-xs px-2 py-1">Cargando…</div>}
        {!isLoading && filtered.length === 0 && (
          <div className="text-slate-500 text-xs px-2 py-1 italic">
            {search ? 'Sin resultados.' : 'Sin proyectos. Creá uno.'}
          </div>
        )}
        {filtered.map((c) => (
          <ClientItem
            key={c.client || '__none__'}
            client={c}
            isExpanded={expanded.has(`c:${c.client}`)}
            onToggle={() => toggle(`c:${c.client}`)}
            expandedSet={expanded}
            toggle={toggle}
            currentPath={location.pathname}
          />
        ))}
      </div>

      <SidebarSection title="🗂 Bibliotecas globales">
        <SidebarLink to="/clients" emoji="🤝" label="Clientes" hint="agrupan proyectos" currentPath={location.pathname} />
        <SidebarLink to="/character-creator" emoji="👥" label="Personajes" hint="reutilizables" currentPath={location.pathname} />
        <SidebarLink to="/backgrounds" emoji="🖼" label="Fondos" hint="image_2d" currentPath={location.pathname} />
      </SidebarSection>

      <SidebarSection title="🛠 Herramientas">
        <SidebarLink to="/agents" emoji="🎭" label="Catálogo de agentes" hint="20 expertos" currentPath={location.pathname} />
        <SidebarLink to="/keyframe-sequence" emoji="🎞" label="Video por keyframes" hint="Seedance × N" currentPath={location.pathname} />
        <SidebarLink to="/video" emoji="🎬" label="Video de presentación" hint="spot por spec (Remotion)" currentPath={location.pathname} />
        <SidebarLink to="/jobs" emoji="📋" label="Trabajos" hint="generaciones + renders" currentPath={location.pathname} />
      </SidebarSection>

      <SidebarSection title="📊 Admin">
        <SidebarLink to="/costs" emoji="📊" label="Dashboard de gastos" hint="multi-provider" currentPath={location.pathname} />
        <SidebarLink to="/billing" emoji="💰" label="Billing fal.ai" hint="sync + CSV" currentPath={location.pathname} />
        <SidebarLink to="/admin/elevenlabs-account" emoji="🟢" label="Cuenta ElevenLabs" hint="consumo total del plan" currentPath={location.pathname} />
      </SidebarSection>

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreated={(slug) => navigate(`/projects/${slug}`)}
        />
      )}

      <VersionBadge />
    </aside>
  );
}

/**
 * Badge de versión al pie del sidebar. El operador puede leerlo para decir
 * "qué versión estoy viendo" al reportar bugs (pedido tras incidente Julia 2026-06-02).
 * Commit SHA viene de VITE_APP_VERSION (Dockerfile → RAILWAY_GIT_COMMIT_SHA);
 * la fecha de build de __BUILD_TIME__ (vite define).
 */
function VersionBadge() {
  const sha = (import.meta.env.VITE_APP_VERSION as string | undefined)?.slice(0, 7);
  const buildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '';
  return (
    <div
      className="border-t border-slate-800 px-3 py-1.5 text-[10px] text-slate-500 font-mono shrink-0"
      title={`Build: ${buildTime} UTC${sha ? ` · commit ${sha}` : ''}`}
    >
      xVision {sha ? `· ${sha}` : '· dev'} {buildTime && `· ${buildTime}`}
    </div>
  );
}

function SidebarSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-slate-800 px-2 py-2">
      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-500">{title}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function SidebarLink({
  to,
  emoji,
  label,
  hint,
  currentPath,
}: {
  to: string;
  emoji: string;
  label: string;
  hint?: string;
  currentPath: string;
}) {
  const active = currentPath === to || currentPath.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-slate-800 transition-colors ${
        active ? 'text-vision bg-slate-800/50' : 'text-slate-300'
      }`}
    >
      <span className="text-base shrink-0">{emoji}</span>
      <span className="flex-1 truncate">{label}</span>
      {hint && <span className="text-[10px] text-slate-500 shrink-0">{hint}</span>}
    </Link>
  );
}

function ClientItem({
  client,
  isExpanded,
  onToggle,
  expandedSet,
  toggle,
  currentPath,
}: {
  client: TreeClientNode;
  isExpanded: boolean;
  onToggle: () => void;
  expandedSet: Set<string>;
  toggle: (key: string) => void;
  currentPath: string;
}) {
  const label = client.client || '(sin cliente)';
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-1 px-2 py-1 text-slate-200 hover:bg-slate-800 rounded text-left"
      >
        <Caret open={isExpanded} />
        <span className="text-xs uppercase tracking-wide font-medium truncate">{label}</span>
        <span className="ml-auto text-[10px] text-slate-500">{client.projects.length}</span>
      </button>
      {isExpanded && (
        <div className="ml-3 border-l border-slate-800 pl-1 space-y-0.5 mt-0.5">
          {client.projects.map((p) => (
            <ProjectItem
              key={p.id}
              project={p}
              isExpanded={expandedSet.has(`p:${p.id}`)}
              onToggle={() => toggle(`p:${p.id}`)}
              expandedSet={expandedSet}
              toggle={toggle}
              currentPath={currentPath}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectItem({
  project,
  isExpanded,
  onToggle,
  expandedSet,
  toggle,
  currentPath,
}: {
  project: TreeProjectNode;
  isExpanded: boolean;
  onToggle: () => void;
  expandedSet: Set<string>;
  toggle: (key: string) => void;
  currentPath: string;
}) {
  const isActive = currentPath === `/projects/${project.slug}`;
  return (
    <div>
      <div className="flex items-center group">
        <button
          onClick={onToggle}
          className="p-1 text-slate-500 hover:text-slate-300"
          title={isExpanded ? 'Contraer' : 'Expandir'}
        >
          <Caret open={isExpanded} />
        </button>
        <Link
          to={`/projects/${project.slug}`}
          className={`flex-1 px-2 py-1 truncate rounded ${
            isActive ? 'bg-vision-dark/40 text-white' : 'text-slate-300 hover:bg-slate-800'
          }`}
        >
          📁 {project.name}
        </Link>
        <Link
          to={`/projects/${project.slug}?create=1`}
          className="opacity-0 group-hover:opacity-100 px-1.5 text-slate-500 hover:text-vision text-base shrink-0"
          title="Crear nuevo asset / instance en este proyecto"
        >
          ＋
        </Link>
      </div>
      {isExpanded && (
        <div className="ml-4 border-l border-slate-800 pl-1 space-y-0.5 mt-0.5">
          <SectionGroups
            project={project}
            expandedSet={expandedSet}
            toggle={toggle}
            currentPath={currentPath}
          />
        </div>
      )}
    </div>
  );
}

function SectionGroups({
  project,
  expandedSet,
  toggle,
  currentPath,
}: {
  project: TreeProjectNode;
  expandedSet: Set<string>;
  toggle: (key: string) => void;
  currentPath: string;
}) {
  // Agrupamos los assets ya filtrados por search (vienen así del padre) en las
  // 9 secciones canónicas. Las secciones aparecen siempre — vacías en gris,
  // con (0), para que el operador sepa que existen.
  const bySection = useMemo<Record<SectionKey, TreeAssetNode[]>>(() => {
    const m: Record<SectionKey, TreeAssetNode[]> = {
      personajes: [], fondos: [], panoramas: [], composiciones: [],
      series: [], videos: [], lipsync: [], editor: [], libre: [],
    };
    for (const a of project.assets) {
      m[getSectionForSubtype(a.subtype)].push(a);
    }
    return m;
  }, [project.assets]);

  return (
    <>
      {SECTIONS.map((sec) => (
        <SectionGroup
          key={sec.key}
          section={sec}
          assets={bySection[sec.key]}
          projectSlug={project.slug}
          isExpanded={expandedSet.has(`s:${project.id}:${sec.key}`)}
          onToggle={() => toggle(`s:${project.id}:${sec.key}`)}
          currentPath={currentPath}
        />
      ))}
    </>
  );
}

function SectionGroup({
  section,
  assets,
  projectSlug,
  isExpanded,
  onToggle,
  currentPath,
}: {
  section: SectionDef;
  assets: TreeAssetNode[];
  projectSlug: string;
  isExpanded: boolean;
  onToggle: () => void;
  currentPath: string;
}) {
  const isEmpty = assets.length === 0;
  return (
    <div>
      <button
        onClick={isEmpty ? undefined : onToggle}
        className={`w-full flex items-center gap-1 px-2 py-1 rounded text-left text-[11px] ${
          isEmpty
            ? 'text-slate-600 cursor-default'
            : 'text-slate-400 hover:bg-slate-800 cursor-pointer'
        }`}
        title={isEmpty ? `${section.label}: ${section.hint}` : section.label}
        aria-disabled={isEmpty}
      >
        {isEmpty ? <span className="inline-block w-3" /> : <Caret open={isExpanded} />}
        <span className="text-sm shrink-0">{section.emoji}</span>
        <span className="flex-1 truncate">{section.label}</span>
        <span className="text-[10px] text-slate-500 shrink-0">({assets.length})</span>
      </button>
      {isExpanded && !isEmpty && (
        <div className="ml-3 border-l border-slate-800 pl-1 space-y-0.5 mt-0.5">
          {assets.map((a) => (
            <AssetItem
              key={a.id}
              projectSlug={projectSlug}
              asset={a}
              currentPath={currentPath}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AssetItem({
  projectSlug,
  asset,
  currentPath,
}: {
  projectSlug: string;
  asset: TreeAssetNode;
  currentPath: string;
}) {
  // 1:1 instance ↔ chat. Si ya hay un chat creado, linkeamos directo a /chats/:id
  // (más rápido, no pasa por el redirect de la página Instance). Si no hay
  // chat, click va a /instances/:slug que crea el chat y redirige.
  const session = asset.sessions[0];
  const target = session ? `/chats/${session.id}` : `/projects/${projectSlug}/instances/${asset.slug}`;
  const isActive = currentPath === target;
  const icon = KIND_ICONS[asset.kind] ?? '◆';
  return (
    <Link
      to={target}
      className={`flex items-center gap-1 px-2 py-1 ml-1 rounded text-xs ${
        isActive ? 'bg-vision-dark/40 text-white' : 'text-slate-300 hover:bg-slate-800'
      }`}
      title={session ? `Abrir chat con ${agentLabel(session.agentSlug)}` : 'Crear chat con experto'}
    >
      <span className="shrink-0">{icon}</span>
      <span className="flex-1 truncate">{asset.name}</span>
      {session ? (
        <span className="shrink-0 text-[10px] text-vision/80" title="Chat existente">💬</span>
      ) : (
        <span className="shrink-0 text-[10px] text-slate-600 group-hover:text-slate-400" title="Sin chat">＋</span>
      )}
    </Link>
  );
}

function Caret({ open }: { open: boolean }) {
  return (
    <span
      className={`inline-block w-3 text-slate-500 text-[10px] transition-transform ${
        open ? 'rotate-90' : ''
      }`}
    >
      ▶
    </span>
  );
}
